import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Add, FolderOpen } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useGeneratorContext } from '../../contexts/GeneratorStateContext';
import { characterRenderSrc } from '../../core/Build';
import { getCharacterClass } from '../../core/characterUtils';
import { localGenPath } from '../../utils/buildRoutes';
import './MyBuildsNavMenu.css';

const RECENT_LIMIT = 6;
const DEFAULT_LVL = 200;
const DATE_OPTS = { day: '2-digit', month: 'short' };
const CLOSE_DELAY_MS = 120;
const MENU_Z = 10000;

const TXT = {
    myBuilds: { fr: 'Mes stuffs', en: 'My builds', de: 'Meine Builds', es: 'Mis builds', pt: 'Minhas builds' },
    newBuild: { fr: 'Nouveau stuff', en: 'New build', de: 'Neuer Build', es: 'Nueva build', pt: 'Nova build' },
    unnamed: { fr: 'Sans nom', en: 'Unnamed', de: 'Ohne Namen', es: 'Sin nombre', pt: 'Sem nome' },
    empty: { fr: 'Aucun stuff', en: 'No builds', de: 'Keine Builds', es: 'Ninguna build', pt: 'Nenhuma build' },
    lvl: { fr: 'Niv.', en: 'Lv.', de: 'St.', es: 'Niv.', pt: 'Nív.' },
};

function t(key, lang) {
    const row = TXT[key];
    return row?.[lang] || row?.en || row?.fr || '';
}

function lastModMs(entry) {
    const iso = entry?.updatedAt || entry?.createdAt;
    if (!iso) return 0;
    const ms = Date.parse(iso);
    return Number.isFinite(ms) ? ms : 0;
}

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString(undefined, DATE_OPTS);
    } catch {
        return '';
    }
}

function entryClassIcon(entry) {
    const raw = entry?.build?.characterClass;
    const name = typeof raw === 'string' ? raw : raw?.name;
    return getCharacterClass(name)?.icon || '';
}

function entryRenderSrc(entry) {
    return characterRenderSrc(entry?.build?.characterRender);
}

function BuildThumb({ entry }) {
    const renderSrc = entryRenderSrc(entry);
    const classIcon = entryClassIcon(entry);
    const [useFallback, setUseFallback] = useState(!renderSrc);

    if (!useFallback && renderSrc) {
        return (
            <span className="mybuilds-nav-thumb mybuilds-nav-thumb--render" aria-hidden="true">
                <img
                    src={renderSrc}
                    alt=""
                    draggable={false}
                    onError={() => setUseFallback(true)}
                />
            </span>
        );
    }

    return (
        <span className="mybuilds-nav-thumb mybuilds-nav-thumb--icon" aria-hidden="true">
            {classIcon ? (
                <img src={classIcon} alt="" draggable={false} />
            ) : (
                <FolderOpen fontSize="inherit" />
            )}
        </span>
    );
}

/**
 * Primary-nav "My builds" item with a hover submenu:
 * New build + recent builds in a compact horizontal strip.
 * Menu is portaled to body so blur/z-index work above page content.
 */
export default function MyBuildsNavMenu({ lang, base }) {
    const navigate = useNavigate();
    const rootRef = useRef(null);
    const menuRef = useRef(null);
    const closeTimerRef = useRef(null);
    const { isAuthenticated, authState } = useAuth();
    const {
        builds,
        activeBuildId,
        storageMode,
        openBuild,
        createBuild,
        refreshBuilds,
        getBuildPath,
    } = useGeneratorContext();
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState(null);

    const isAuthLoading = authState === 'loading';
    const accountBuildsUnavailable = isAuthenticated && storageMode === 'offline-auth-fallback';

    const recent = useMemo(() => {
        if (isAuthLoading || accountBuildsUnavailable) return [];
        const rows = isAuthenticated
            ? builds.filter((entry) => entry?.scope === 'remote')
            : builds;
        return [...rows]
            .sort((a, b) => lastModMs(b) - lastModMs(a))
            .slice(0, RECENT_LIMIT);
    }, [accountBuildsUnavailable, builds, isAuthLoading, isAuthenticated]);

    const clearCloseTimer = () => {
        if (closeTimerRef.current != null) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const syncCoords = () => {
        const el = rootRef.current;
        const nav = el?.closest('.top-navbar');
        if (!el || !nav) return;
        const triggerRect = el.getBoundingClientRect();
        const navRect = nav.getBoundingClientRect();
        setCoords({
            top: navRect.bottom,
            left: triggerRect.left + triggerRect.width / 2,
        });
    };

    const openMenu = () => {
        clearCloseTimer();
        syncCoords();
        setOpen(true);
        if (!isAuthLoading) refreshBuilds();
    };

    const scheduleClose = () => {
        clearCloseTimer();
        closeTimerRef.current = setTimeout(() => {
            setOpen(false);
            closeTimerRef.current = null;
        }, CLOSE_DELAY_MS);
    };

    const onTriggerBlur = (e) => {
        const next = e.relatedTarget;
        if (rootRef.current?.contains(next) || menuRef.current?.contains(next)) return;
        scheduleClose();
    };

    useEffect(() => () => clearCloseTimer(), []);

    useEffect(() => {
        if (!open) return undefined;
        const onReposition = () => syncCoords();
        window.addEventListener('resize', onReposition);
        window.addEventListener('scroll', onReposition, true);
        return () => {
            window.removeEventListener('resize', onReposition);
            window.removeEventListener('scroll', onReposition, true);
        };
    }, [open]);

    const goToEntry = async (id) => {
        if (busy || !id) return;
        setBusy(true);
        try {
            const entry = await openBuild(id);
            if (!entry) return;
            setOpen(false);
            navigate(getBuildPath(entry, lang) || localGenPath(lang));
        } finally {
            setBusy(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (busy) return;
        setBusy(true);
        try {
            const entry = await createBuild();
            if (!entry) return;
            setOpen(false);
            navigate(getBuildPath(entry, lang) || localGenPath(lang), {
                state: { focusBuildName: true },
            });
        } finally {
            setBusy(false);
        }
    };

    const menu = open && coords && typeof document !== 'undefined'
        ? createPortal(
            <div
                ref={menuRef}
                className="mybuilds-nav-menu"
                role="menu"
                aria-label={t('myBuilds', lang)}
                style={{
                    top: coords.top,
                    left: coords.left,
                    zIndex: MENU_Z,
                }}
                onMouseEnter={openMenu}
                onMouseLeave={scheduleClose}
                onFocusCapture={openMenu}
            >
                <button
                    type="button"
                    className="mybuilds-nav-item mybuilds-nav-item--new"
                    role="menuitem"
                    disabled={busy}
                    onClick={handleCreate}
                >
                    <span className="mybuilds-nav-thumb mybuilds-nav-thumb--new" aria-hidden="true">
                        <Add fontSize="inherit" />
                    </span>
                    <span className="mybuilds-nav-item-name">{t('newBuild', lang)}</span>
                </button>

                {recent.length === 0 ? (
                    <div className="mybuilds-nav-empty">{t('empty', lang)}</div>
                ) : (
                    recent.map((entry) => {
                        const name = entry.name?.trim() || t('unnamed', lang);
                        const lvl = entry.build?.lvl ?? DEFAULT_LVL;
                        const date = formatDate(entry.updatedAt || entry.createdAt);
                        const isActive = entry.id === activeBuildId;
                        const href = getBuildPath(entry, lang);
                        const className = `mybuilds-nav-item${isActive ? ' is-active' : ''}`;
                        const body = (
                            <>
                                <BuildThumb entry={entry} />
                                <span className="mybuilds-nav-item-name">{name}</span>
                                <span className="mybuilds-nav-item-meta">
                                    <span>{t('lvl', lang)} {lvl}</span>
                                    {date ? (
                                        <>
                                            <span className="mybuilds-nav-item-sep" aria-hidden="true">·</span>
                                            <span>{date}</span>
                                        </>
                                    ) : null}
                                </span>
                            </>
                        );
                        // Real href so middle-click / ctrl-click can open a new tab.
                        if (href) {
                            return (
                                <Link
                                    key={entry.id}
                                    to={href}
                                    className={className}
                                    role="menuitem"
                                    title={name}
                                    onClick={() => setOpen(false)}
                                >
                                    {body}
                                </Link>
                            );
                        }
                        return (
                            <button
                                key={entry.id}
                                type="button"
                                className={className}
                                role="menuitem"
                                disabled={busy}
                                onClick={() => goToEntry(entry.id)}
                                title={name}
                            >
                                {body}
                            </button>
                        );
                    })
                )}
            </div>,
            document.body,
        )
        : null;

    return (
        <div
            ref={rootRef}
            className={`mybuilds-nav${open ? ' is-open' : ''}`}
            onMouseEnter={openMenu}
            onMouseLeave={scheduleClose}
            onFocusCapture={openMenu}
            onBlurCapture={onTriggerBlur}
        >
            <NavLink
                to={`${base}/my-builds`}
                className={({ isActive }) => (isActive ? 'active' : '')}
            >
                <FolderOpen fontSize="small" />
                <h6>{t('myBuilds', lang)}</h6>
            </NavLink>
            {menu}
        </div>
    );
}
